import React, { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Crown, ChevronRight, ShieldCheck, Store } from "lucide-react";
import { getSidebarGroups, LOGO_URL } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { ConexionAPI } from "../lib/conexion";
import { fetchContadorNoti } from "../lib/notificaciones";
import { apiAdminTareasPendientes } from "../lib/marketplaceApi";

function GroupTitle({ children }) {
  return (
    <p className="px-6 mb-3 mt-6 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
      {children}
    </p>
  );
}

function Item({ item }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      data-testid={`sidebar-item-${item.key}`}
      className={({ isActive }) =>
        [
          "group flex items-center gap-3 px-6 py-2.5 text-sm font-medium transition-all border-l-4",
          isActive
            ? "bg-white/[0.06] text-white border-brand-gold"
            : "text-slate-300 hover:text-white hover:bg-white/[0.04] border-transparent",
        ].join(" ")
      }
    >
      <Icon
        size={18}
        className="opacity-90 group-hover:opacity-100 transition-opacity"
        strokeWidth={1.8}
      />
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge ? (
        <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-[10px] font-bold bg-brand-red text-white">
          {item.badge}
        </span>
      ) : null}
    </NavLink>
  );
}

export default function Sidebar() {
  const { user } = useAuth();
  const role = (user && user.role) || "cliente";
  const groups = getSidebarGroups(role);
  const [badges, setBadges] = useState({ mensajes: 0, notificaciones: 0 });
  const [mktPend, setMktPend] = useState(0);

  useEffect(() => {
    let activo = true;
    const cargar = async () => {
      const [msgs, notis] = await Promise.all([
        ConexionAPI.mensajesNoLeidos().catch(() => 0),
        fetchContadorNoti().catch(() => 0),
      ]);
      if (activo) setBadges({ mensajes: msgs, notificaciones: notis });
      if (role === "admin") {
        const t = await apiAdminTareasPendientes().catch(() => null);
        if (activo && t) setMktPend(t.total || 0);
      }
    };
    cargar();
    const t = setInterval(cargar, 15000);
    return () => { activo = false; clearInterval(t); };
  }, [role]);

  const withBadges = (items) =>
    items.map((it) => (badges[it.key] ? { ...it, badge: badges[it.key] } : it));

  return (
    <aside
      className="hidden lg:flex fixed left-0 top-0 h-screen w-72 flex-col bg-[#0B1E36] border-r border-white/5 z-40 overflow-y-auto cl-scroll"
      data-testid="sidebar"
    >
      {/* Logo block (mirrors topbar height) */}
      <Link to="/" className="h-24 px-4 flex items-center justify-center border-b border-white/5 bg-white flex-shrink-0" data-testid="sidebar-logo-link">
        <img src={LOGO_URL} alt="ConstruLink" className="w-full max-w-[250px] h-auto object-contain" />
      </Link>

      <div className="flex-1 pb-6">
        {groups.map((group) => (
          <div key={group.title}>
            <GroupTitle>{group.title}</GroupTitle>
            <nav className="flex flex-col">
              {withBadges(group.items).map((item) => (
                <Item key={item.key} item={item} />
              ))}
            </nav>
          </div>
        ))}
        {role === "admin" && (
          <div>
            <GroupTitle>Administración global</GroupTitle>
            <nav className="flex flex-col">
              <Item item={{ label: "Panel Admin", to: "/admin", icon: ShieldCheck, key: "admin" }} />
              <Item item={{ label: "Moderar Marketplace", to: "/admin/marketplace", icon: Store, key: "admin-marketplace", badge: mktPend > 0 ? (mktPend > 99 ? "99+" : mktPend) : undefined }} />
            </nav>
          </div>
        )}
      </div>

      {/* Premium card */}
      <div className="p-4">
        <div
          className="relative overflow-hidden rounded-2xl p-5 border border-brand-gold/30"
          style={{
            background:
              "linear-gradient(135deg, rgba(30,58,138,0.85) 0%, rgba(11,30,54,0.95) 100%)",
          }}
          data-testid="sidebar-premium-card"
        >
          <div
            aria-hidden
            className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-30 blur-2xl"
            style={{ background: "#FBBF24" }}
          />
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-brand-gold/20 flex items-center justify-center">
              <Crown size={18} className="text-brand-gold" />
            </div>
            <p className="text-white font-semibold text-sm">¡Hazte Premium!</p>
          </div>
          <p className="text-slate-300 text-xs leading-relaxed mb-4">
            Más visibilidad, más beneficios y crecimiento para tu negocio.
          </p>
          <Link
            to="/planes"
            data-testid="sidebar-premium-cta"
            className="inline-flex items-center gap-1.5 bg-brand-gold hover:bg-yellow-300 text-[#0B1E36] font-semibold text-xs px-3.5 py-2 rounded-lg transition-colors"
          >
            Ver planes <ChevronRight size={14} />
          </Link>
        </div>
      </div>
    </aside>
  );
}
